import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { parseBatchCode } from "@/lib/qr";
import { InstallButton } from "@/components/InstallButton";
import { toast } from "sonner";
import { api, errMsg, fmtNum, fmtDate } from "@/lib/api";
import { readQueue, enqueueScan, removeQueued, flushQueue, isNetworkError, cacheBatch, cachedBatch, primeBatchCache } from "@/lib/offlineQueue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BuyerInput } from "@/components/BuyerInput";
import { PageHeader } from "@/components/StatCard";
import { Camera, ScanLine, Trash2, Check, X, AlertTriangle, Delete, WifiOff, Wifi, RefreshCw, CloudUpload } from "lucide-react";

const beep = (ok) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination); o.frequency.value = ok ? 1400 : 300; g.gain.value = 0.15;
    o.start(); o.stop(ctx.currentTime + (ok ? 0.08 : 0.25));
  } catch {}
  if (navigator.vibrate) navigator.vibrate(ok ? 40 : [80, 40, 80]);
};

export default function ScanOut() {
  const { can } = useAuth(); const isAdmin = can("sale_price");
  const [params, setParams] = useSearchParams();
  const [code, setCode] = useState("");
  const [current, setCurrent] = useState(null);
  const [qty, setQty] = useState("");
  const [cart, setCart] = useState([]);
  const [destination, setDestination] = useState("");
  const [complete, setComplete] = useState(false);
  const [payment, setPayment] = useState("cash");
  const [flash, setFlash] = useState("");
  const [cam, setCam] = useState(false);
  const [saving, setSaving] = useState(false);
  const scanRef = useRef(null);
  const qtyRef = useRef(null);
  const scannerRef = useRef(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [queue, setQueue] = useState(readQueue());
  const [flushing, setFlushing] = useState(false);

  const doFlush = useCallback(async () => {
    if (!readQueue().length) return;
    setFlushing(true);
    const r = await flushQueue();
    setFlushing(false);
    setQueue(readQueue());
    if (r.sent) toast.success(`${r.sent} transaksi scan tertunda berhasil dikirim`);
  }, []);
  useEffect(() => {
    const on = () => { setOnline(true); doFlush(); };
    const off = () => setOnline(false);
    const qc = () => setQueue(readQueue());
    window.addEventListener("online", on); window.addEventListener("offline", off); window.addEventListener("scan-queue-change", qc);
    const t = setInterval(() => { if (navigator.onLine) doFlush(); }, 30000);
    primeBatchCache(); doFlush();
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); window.removeEventListener("scan-queue-change", qc); clearInterval(t); };
  }, [doFlush]);

  useEffect(() => { if (!current && !cam) scanRef.current?.focus(); }, [current, cam, cart]);

  const lookup = useCallback(async (raw) => {
    const c = parseBatchCode(raw);
    if (!c) return;
    try {
      const r = await api.get(`/inventory/by-code/${encodeURIComponent(c)}`);
      cacheBatch(r.data);
      if (r.data.quantity_mc <= 0) { beep(false); setFlash("err"); toast.error(`${r.data.batch_code} stok habis`); }
      else { beep(true); setFlash("ok"); setCurrent(r.data); setQty(""); setTimeout(() => qtyRef.current?.focus(), 50); }
    } catch (e) {
      const cached = isNetworkError(e) ? cachedBatch(c) : null;
      if (cached) { beep(true); setFlash("ok"); setCurrent({ ...cached, _offline: true }); setQty(""); toast.warning("Offline — memakai data batch tersimpan"); setTimeout(() => qtyRef.current?.focus(), 50); }
      else { beep(false); setFlash("err"); toast.error(isNetworkError(e) ? "Tidak ada koneksi dan batch belum pernah dimuat" : errMsg(e)); }
    }
    setCode("");
    setTimeout(() => setFlash(""), 600);
  }, []);

  useEffect(() => {
    const b = params.get("batch");
    if (b) { lookup(b); setParams({}, { replace: true }); }
  }, [params, setParams, lookup]);

  useEffect(() => {
    if (!cam) { scannerRef.current?.stop().catch(() => {}); scannerRef.current = null; return; }
    let stopped = false;
    import("html5-qrcode").then(({ Html5Qrcode }) => {
      if (stopped) return;
      const s = new Html5Qrcode("scan-camera");
      scannerRef.current = s;
      s.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 }, (text) => { if (!current) { lookup(text); } }, () => {}).catch((e) => { toast.error("Kamera tidak tersedia: " + (e?.message || e)); setCam(false); });
    });
    return () => { stopped = true; scannerRef.current?.stop().catch(() => {}); scannerRef.current = null; };
  }, [cam, lookup, current]);

  const addToCart = () => {
    const q = Number(qty);
    if (!current || !(q > 0)) return;
    const existing = cart.find((r) => r.batch_code === current.batch_code);
    const total = (existing?.quantity_mc || 0) + q;
    if (total > current.quantity_mc) { beep(false); toast.error(`Melebihi sisa stok (${fmtNum(current.quantity_mc)} ${current.unit || "MC"})`); return; }
    setCart((p) => existing ? p.map((r) => (r.batch_code === current.batch_code ? { ...r, quantity_mc: total } : r)) : [...p, { ...current, quantity_mc: q, stock: current.quantity_mc }]);
    setCurrent(null); setQty("");
  };
  const key = (k) => { if (k === "del") setQty((q) => q.slice(0, -1)); else if (k === "," ) { if (current?.unit === "KG" && !qty.includes(".")) setQty((q) => (q || "0") + "."); } else setQty((q) => (q === "0" ? k : q + k)); };

  const save = async () => {
    setSaving(true);
    const payload = { destination, note: "Mode scan", items: cart.map((c) => ({ batch_code: c.batch_code, quantity_mc: c.quantity_mc, unit_price: isAdmin && c.unit_price ? Number(c.unit_price) : undefined })), complete: isAdmin && complete, payment };
    if (payload.complete && !destination.trim()) { toast.error("Tujuan / pembeli wajib diisi untuk menyelesaikan langsung"); setSaving(false); return; }
    try {
      if (!navigator.onLine) throw { offline: true };
      const r = await api.post("/transactions/scan-out", payload);
      beep(true);
      toast.success(r.data.completed ? `${cart.length} batch keluar disimpan & selesai (${r.data.group_no}).` : `${cart.length} batch keluar disimpan (${r.data.group_no}). Menunggu kelengkapan admin.`, { action: { label: "Nota gabungan", onClick: () => window.open(`/nota-grup/${r.data.group_no}`, "_blank") }, duration: 8000 });
      setCart([]); setDestination(""); setComplete(false);
    } catch (e) {
      if (e?.offline || isNetworkError(e)) {
        enqueueScan(payload); beep(true);
        toast.warning(`Tidak ada sinyal — ${cart.length} batch disimpan di antrian offline dan akan dikirim otomatis saat koneksi pulih.`, { duration: 8000 });
        setCart([]); setDestination(""); setQueue(readQueue());
      } else { beep(false); toast.error(errMsg(e)); }
    } finally { setSaving(false); }
  };

  const nearExpiry = current?.expiry_status && current.expiry_status !== "ok";
  return (
    <div data-testid="scan-out-page" className="max-w-xl mx-auto">
      <PageHeader title="Mode Scan Keluar" subtitle="Scan label QR batch, ketik jumlah, simpan. Admin melengkapi pembeli & harga kemudian." />
      <div className={`mb-3 flex items-center justify-between rounded-xl px-3 py-2 text-xs font-semibold ${online ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-rose-50 text-rose-700 border border-rose-200"}`} data-testid="scan-connection">
        <span className="inline-flex items-center gap-1.5">{online ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}{online ? "Online — tersambung ke server" : "Offline — scan tetap bisa, data disimpan di antrian"}</span>
        {queue.length > 0 && <span className="inline-flex items-center gap-1" data-testid="scan-queue-count"><CloudUpload className="h-4 w-4" /> {queue.length} menunggu kirim</span>}
        <span className="inline-flex items-center gap-2"><InstallButton compact /><Link to="/pasang" className="underline underline-offset-2 opacity-80 hover:opacity-100" data-testid="link-scan-install">Cara pasang di HP</Link></span>
      </div>
      {queue.length > 0 && (
        <div className="card-aqua p-3 mb-4 border border-amber-200" data-testid="scan-queue">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-slate-800">Antrian offline</div>
            <Button size="sm" variant="outline" className="h-8 rounded-full border-amber-300 text-amber-700" onClick={doFlush} disabled={flushing || !online} data-testid="btn-queue-flush"><RefreshCw className={`h-3.5 w-3.5 mr-1 ${flushing ? "animate-spin" : ""}`} /> Kirim sekarang</Button>
          </div>
          <ul className="divide-y divide-amber-50 text-xs">
            {queue.map((q, i) => (
              <li key={q.client_id} className="py-1.5 flex items-center gap-2" data-testid={`queue-row-${i}`}>
                <div className="flex-1 min-w-0"><div className="text-slate-700">{q.items.length} batch • {q.items.reduce((a, it) => a + it.quantity_mc, 0)} unit{q.destination ? ` • ${q.destination}` : ""}</div><div className="text-slate-400">{new Date(q.queued_at).toLocaleString("id-ID")}{q.last_error && <span className="text-rose-600 ml-1">• {q.last_error}</span>}</div></div>
                <button type="button" onClick={() => { if (window.confirm("Hapus dari antrian? Data ini tidak akan dikirim.")) { removeQueued(q.client_id); setQueue(readQueue()); } }} className="p-1 text-rose-600 hover:bg-rose-50 rounded-md" data-testid={`btn-queue-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className={`card-aqua p-4 mb-4 transition-colors ${flash === "ok" ? "ring-4 ring-emerald-300" : flash === "err" ? "ring-4 ring-rose-300" : ""}`}>
        <form onSubmit={(e) => { e.preventDefault(); lookup(code); }} className="flex gap-2">
          <div className="relative flex-1">
            <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-amber-600" />
            <Input ref={scanRef} value={code} onChange={(e) => setCode(e.target.value)} placeholder="Scan / ketik kode batch lalu Enter" className="pl-10 h-12 text-lg font-mono" autoFocus data-testid="input-scan-code" />
          </div>
          <Button type="button" variant={cam ? "default" : "outline"} className={`h-12 rounded-xl ${cam ? "bg-amber-600 hover:bg-amber-700" : "border-amber-300 text-amber-700"}`} onClick={() => setCam((c) => !c)} data-testid="btn-toggle-camera"><Camera className="h-5 w-5" /></Button>
        </form>
        {cam && <div id="scan-camera" className="mt-3 rounded-xl overflow-hidden bg-black" data-testid="scan-camera" />}
      </div>

      {current && (
        <div className="card-aqua p-4 mb-4 border-2 border-amber-300" data-testid="scan-batch-card">
          <div className="flex items-start justify-between">
            <div>
              <div className="font-mono font-extrabold text-sky-800 text-lg" data-testid="scan-batch-code">{current.batch_code}</div>
              <div className="font-bold text-slate-900">{current.species_name} <span className="font-normal text-slate-600">{current.size}</span></div>
              <div className="text-xs text-slate-500">{current.location} • masuk {fmtDate(current.entry_date)} • sisa <b className="text-slate-800">{fmtNum(current.quantity_mc)} {current.unit || "MC"}</b></div>
              {nearExpiry && <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full" data-testid="scan-expiry-warning"><AlertTriangle className="h-3 w-3" /> Mendekati / lewat kadaluarsa ({fmtDate(current.expiry_date)})</div>}
            </div>
            <button type="button" onClick={() => setCurrent(null)} className="p-1 text-slate-400 hover:text-rose-600" data-testid="btn-scan-cancel"><X className="h-5 w-5" /></button>
          </div>
          <div className="mt-3">
            <Input ref={qtyRef} value={qty} onChange={(e) => setQty(e.target.value.replace(",", ".").replace(/[^\d.]/g, ""))} onKeyDown={(e) => e.key === "Enter" && addToCart()} inputMode="decimal" placeholder={`Jumlah keluar (${current.unit || "MC"})`} className="h-14 text-2xl font-mono text-center" data-testid="input-scan-qty" />
            <div className="grid grid-cols-3 gap-2 mt-2">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", ",", "0", "del"].map((k) => (
                <button key={k} type="button" onClick={() => key(k)} className={`h-12 rounded-xl text-xl font-bold ${k === "del" ? "bg-rose-50 text-rose-600" : k === "," ? "bg-slate-100 text-slate-500" : "bg-sky-50 text-sky-900 hover:bg-sky-100"}`} data-testid={`key-${k}`}>{k === "del" ? <Delete className="h-5 w-5 mx-auto" /> : k}</button>
              ))}
            </div>
            <Button type="button" onClick={addToCart} disabled={!(Number(qty) > 0)} className="w-full h-12 mt-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-base" data-testid="btn-scan-add"><Check className="h-5 w-5 mr-1" /> Tambah ke keranjang</Button>
          </div>
        </div>
      )}

      <div className="card-aqua p-4" data-testid="scan-cart">
        <div className="flex items-center justify-between mb-2"><div className="font-semibold text-slate-800">Keranjang keluar</div><span className="text-xs text-slate-500" data-testid="scan-cart-count">{cart.length} batch</span></div>
        {cart.length === 0 && <div className="text-sm text-slate-400 py-6 text-center" data-testid="scan-cart-empty">Belum ada batch. Scan label untuk mulai.</div>}
        <ul className="divide-y divide-sky-50">
          {cart.map((c, i) => (
            <li key={c.batch_code} className="py-2 flex items-center gap-3" data-testid={`scan-cart-row-${i}`}>
              <div className="flex-1 min-w-0"><div className="font-mono text-xs font-bold text-sky-800">{c.batch_code}</div><div className="text-sm text-slate-700 truncate">{c.species_name} {c.size}</div></div>
              <div className="font-mono font-bold text-amber-700">{fmtNum(c.quantity_mc)} <span className="text-xs text-slate-400">{c.unit || "MC"}</span></div>
              {isAdmin && complete && <Input value={c.unit_price || ""} onChange={(e) => setCart((p) => p.map((r, idx) => (idx === i ? { ...r, unit_price: e.target.value.replace(/[^\d]/g, "") } : r)))} inputMode="numeric" placeholder={`Harga/${c.unit || "MC"}`} className="w-28 h-9 text-right font-mono" data-testid={`input-cart-price-${i}`} />}
              <button type="button" onClick={() => setCart((p) => p.filter((_, idx) => idx !== i))} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-md" data-testid={`btn-cart-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
        {cart.length > 0 && (
          <div className="mt-3 space-y-2">
            <BuyerInput value={destination} onChange={setDestination} placeholder={complete ? "Tujuan / pembeli (wajib)" : "Tujuan / pembeli (opsional)"} testId="input-scan-destination" />
            {isAdmin && (
              <div className="rounded-xl border border-sky-100 bg-sky-50/60 p-3 space-y-2" data-testid="scan-admin-complete">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-800 cursor-pointer"><input type="checkbox" checked={complete} onChange={(e) => setComplete(e.target.checked)} className="h-4 w-4 accent-sky-600" data-testid="chk-scan-complete" /> Lengkapi sekarang (pembeli & harga) — langsung selesai</label>
                {complete && <select value={payment} onChange={(e) => setPayment(e.target.value)} className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="select-scan-payment"><option value="cash">Pembayaran: Tunai</option><option value="credit">Pembayaran: Piutang / Kredit</option></select>}
                {complete && <div className="text-xs text-slate-500">Isi harga jual per batch di keranjang. Baris tanpa harga tetap disimpan tanpa jurnal penjualan.</div>}
              </div>
            )}
            <Button onClick={save} disabled={saving} className="w-full h-12 rounded-xl bg-amber-600 hover:bg-amber-700 text-base" data-testid="btn-scan-save">{saving ? "Menyimpan..." : complete ? `Simpan & Selesaikan (${cart.length} batch)` : `Simpan Keluar (${cart.length} batch)`}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
